import { JsonObject } from "type-fest";
import { QueryFunctionContext } from "react-query";

import { getHightouchRegionConfig } from "./regions";
import { unauthenticatedRoutes } from "src/components/router/constants";
import {
  FAST_OPERATIONS,
  UNSAFE_OPERATIONS,
  type OperationName,
} from "./fast-operations";
import {
  PermissionsV2Document,
  type PermissionsV2QueryVariables,
} from "src/graphql/types";
import { load as loadPermission } from "./permission-loader";

/**
 * Pre-compiled regex to extract operation type and name from GraphQL queries.
 * Handles leading whitespace and comments before the operation definition.
 * Matches: "query OperationName", "mutation OperationName", or "subscription OperationName"
 */
const OPERATION_REGEX =
  /^\s*(?:#[^\n]*\n\s*)*(query|mutation|subscription)\s+(\w+)/;

export const basicApiEndpoint = import.meta.env.VITE_API_BASE_URL as string;

/**
 * Endpoint for region-safe ("fast") operations — API/Hasura colocated with the
 * control-plane DB primary, avoiding cross-region API↔DB roundtrips.
 *
 * This is `api.hightouch.com` rather than a pinned regional hostname so that
 * fast traffic follows the Route 53 record that gets repointed during a
 * control-plane regional failover (e.g. us-east-1 → us-west-2). Both resolve
 * to the same ALB in normal operation.
 */
export const fastApiEndpoint = basicApiEndpoint;

type ApiEndpointResolution = {
  endpoint: string;
  /** True when an `UNSAFE_OPERATIONS` op had an `isFastEndpoint:true` request overridden to regional. */
  forcedRegionalDespiteFastRequest: boolean;
};

/**
 * Decide which API endpoint a GraphQL operation hits.
 *
 * Precedence: unsafe denylist (always regional) > read-replica strategy pin
 * (query ops when strategy is `"operation-typed"`) > explicit `isFastEndpoint`
 * override > `FAST_OPERATIONS` registry > regional default. The denylist sits
 * above the override so a call site cannot force a region-unsafe op onto the
 * shared fast endpoint.
 *
 * Pure (no I/O, no env reads) so the routing precedence is unit-testable.
 */
export function resolveApiEndpoint({
  operationName,
  operationType,
  isFastEndpoint,
  fastApiEndpoint,
  regionalApiEndpoint,
  readReplicaStrategy,
}: {
  operationName: OperationName | undefined;
  operationType: "query" | "mutation" | "subscription" | undefined;
  isFastEndpoint: boolean | undefined;
  fastApiEndpoint: string;
  regionalApiEndpoint: string;
  readReplicaStrategy: string | undefined;
}): ApiEndpointResolution {
  if (operationName && UNSAFE_OPERATIONS.has(operationName)) {
    return {
      endpoint: regionalApiEndpoint,
      forcedRegionalDespiteFastRequest: isFastEndpoint === true,
    };
  }
  // Read-replica experiment (controlPlaneReadReplicaStrategy): under the
  // operation-typed arm every query operation goes to the regional API so
  // the backend can serve it from the regional replica. Mutations and any
  // other strategy value (including flag off / LD not loaded) are untouched.
  if (readReplicaStrategy === "operation-typed" && operationType === "query") {
    return {
      endpoint: regionalApiEndpoint,
      forcedRegionalDespiteFastRequest: false,
    };
  }
  if (isFastEndpoint !== undefined) {
    return {
      endpoint: isFastEndpoint ? fastApiEndpoint : regionalApiEndpoint,
      forcedRegionalDespiteFastRequest: false,
    };
  }
  if (operationName && FAST_OPERATIONS.has(operationName)) {
    return {
      endpoint: fastApiEndpoint,
      forcedRegionalDespiteFastRequest: false,
    };
  }
  return {
    endpoint: regionalApiEndpoint,
    forcedRegionalDespiteFastRequest: false,
  };
}

export class HttpError extends Error {
  constructor(
    public readonly code: number,
    message: string,
    public readonly body: Record<string, any> = {},
  ) {
    super(message);
  }
}

export class GraphQlError extends Error {
  extensions: JsonObject;

  constructor(message: string, extensions: JsonObject = {}) {
    super(message);
    this.extensions = extensions;
  }
}

export const isFailedResponse = (
  res: Response,
): res is Response & { ok: false } => {
  return !res.ok;
};

/**
 * Converts a response to Error object.
 */
export const responseToError = async (
  res: Response & { ok: false },
): Promise<HttpError> => {
  const contentType = res.headers.get("content-type");

  if (contentType && contentType.includes("application/json")) {
    const content = await res.json();
    return new HttpError(res.status, content?.message ?? "Error", content);
  }

  return new HttpError(res.status, await res.text());
};

export type FetcherFetchOptions = {
  /**
   * Set `keepalive: true` on the underlying `fetch()` so the request can
   * survive page unload (visibilitychange → hidden, pagehide). Used by
   * fire-and-forget flushes from `beforeunload`-style handlers where we can't
   * await but still need the request to reach the server.
   */
  keepalive?: boolean;
};

export const fetcher = <TData, TVariables>(
  query: string,
  variables?: TVariables,
  options?: RequestInit["headers"],
  fetchOptions?: FetcherFetchOptions,
): ((context?: QueryFunctionContext) => Promise<TData>) => {
  return async (context) => {
    if (
      query === PermissionsV2Document &&
      context?.meta?.usePermissionsLoader
    ) {
      const value = await loadPermission(
        variables as unknown as PermissionsV2QueryVariables,
      );
      return {
        isResourceAuthorized: { isAuthorized: value },
      } as unknown as TData;
    }

    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      "x-should-compress": "true",
    };
    if (options) {
      new Headers(options).forEach((value, name) => {
        headers[name] = value;
      });
    }

    // Get API endpoint
    const apiRegion = window.Hightouch?.apiRegion;
    const regionalApiEndpoint = apiRegion
      ? getHightouchRegionConfig(apiRegion).apiEndpoint
      : basicApiEndpoint;
    const workspaceId = window.Hightouch?.workspaceId;
    const spaceId = window.Hightouch?.spaceId;

    if (workspaceId) {
      headers["X-Hightouch-Requested-Workspace-Id"] = String(workspaceId);
    }

    // Include space ID for filtering (null means no space restriction)
    if (spaceId) {
      headers["X-Hightouch-Space-Id"] = String(spaceId);
    }

    if (context?.meta?.isAdmin) {
      headers["X-Hasura-Role"] = "cs_admin";
    }

    const matches = query.match(OPERATION_REGEX);
    const operationName = matches?.[2] as OperationName | undefined;
    const resolverType = matches?.[1] as
      | "query"
      | "mutation"
      | "subscription"
      | undefined;

    // By default, API calls get routed to the workspace's region,
    // but if the requests don't need to access customer data (query previews, etc.),
    // they can be routed to the control-plane primary region (via the fast
    // endpoint) - this avoids long roundtrips between api/hasura and the db
    const { endpoint: apiEndpoint, forcedRegionalDespiteFastRequest } =
      resolveApiEndpoint({
        operationName,
        operationType: resolverType,
        isFastEndpoint: context?.meta?.isFastEndpoint,
        fastApiEndpoint,
        regionalApiEndpoint,
        readReplicaStrategy: window.Hightouch?.readReplicaStrategy,
      });

    if (import.meta.env.DEV && forcedRegionalDespiteFastRequest) {
      console.error(
        `[fast-routing] "${operationName}" is region-unsafe; ignoring isFastEndpoint:true and routing regional`,
      );
    }
    const resolverName = matches?.[2];
    if (resolverType && resolverName) {
      headers["X-Hightouch-resolver-type"] = resolverType;
      headers["X-Hightouch-resolver-name"] = resolverName;
    }
    headers["X-Hightouch-Client-Page"] = window.location.pathname;

    if (import.meta.env.DEV) {
      const { applyGraphqlThrottle } = await import("src/lib/graphql-throttle");
      await applyGraphqlThrottle();
    }

    const res = await fetch(`${apiEndpoint}/v1/graphql`, {
      method: "POST",
      headers,
      body: JSON.stringify({
        query,
        variables,
        ...(operationName ? { operationName } : {}),
      }),
      credentials: "include",
      signal: context?.signal,
      ...(fetchOptions?.keepalive ? { keepalive: true } : {}),
    });

    if (res.status === 401) {
      if (
        !unauthenticatedRoutes.some((path) =>
          window.location.pathname.includes(path),
        )
      ) {
        window.location.href = `${window.location.origin}/login${
          window.location.pathname !== "/"
            ? `?returnTo=${window.location.pathname}`
            : ""
        }`;
      }
    }

    // The body is read as text and parsed by hand so a non-JSON or empty body,
    // such as a proxy error page or a truncated response, becomes an HttpError.
    const rawBody = await res.text();
    let json:
      | {
          errors?: Array<{ message: string; extensions?: JsonObject }>;
          data: TData;
          message?: string;
        }
      | undefined;
    if (rawBody) {
      try {
        json = JSON.parse(rawBody);
      } catch {
        json = undefined;
      }
    }

    if (json === undefined) {
      throw new HttpError(
        res.status,
        rawBody
          ? `Received a non-JSON response from the server (status ${res.status})`
          : `Received an empty response from the server (status ${res.status})`,
      );
    }

    if (json.errors) {
      const error = json.errors[0];

      if (!error) {
        throw new Error("Error");
      }

      // Every query fails until the user signs in through SSO, so send them
      // somewhere that says so instead of a page rendering errors.
      if (
        res.status === 403 &&
        error.extensions?.code === "SSO_REQUIRED" &&
        !window.location.pathname.startsWith("/sso-required")
      ) {
        const slug = error.extensions?.organizationSlug;
        window.location.href = `${window.location.origin}/sso-required${
          typeof slug === "string" && slug
            ? `?org=${encodeURIComponent(slug)}`
            : ""
        }`;
      }

      throw new GraphQlError(error.message, error.extensions);
    }

    // API middleware rejections, such as the 409 for a workspace mismatch,
    // carry a JSON body without `errors`; resolving would hand callers no data.
    if (!res.ok) {
      throw new HttpError(res.status, json.message ?? "Error", json);
    }
    return json.data;
  };
};
